import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { FormFooter } from '@/components/ui/form-footer';
import { RadioGroup, RadioOption } from '@/components/ui/radio-group';
import {
  FLAG_REASONS,
  flagSupplier,
  type FlagReason,
  type Supplier,
  type WriteOutcome,
} from './api';

/**
 * «Segnala un problema» (board 3.8): a sheet from the bottom on a phone, from the side on a wide
 * screen. A reason (the selection is blue, #952 S1) and a note, required with «Altro». It reaches
 * Gestione and stays on the page until Gestione resolves it.
 */
export function ReportProblemSheet({
  open,
  onOpenChange,
  supplier,
  onDone,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly supplier: Supplier;
  readonly onDone: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const formId = useId();
  const noteId = useId();
  const [reason, setReason] = useState<FlagReason | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(null);
      setNote('');
      setTried(false);
      setFailed(false);
    }
  }, [open]);

  const noteMissing = reason === 'other' && note.trim() === '';

  async function submit() {
    setTried(true);
    if (reason === null || noteMissing || sending) return;
    setSending(true);
    setFailed(false);
    const outcome = await flagSupplier(supplier.id, reason, note.trim());
    setSending(false);
    if (!outcome.ok) {
      setFailed(true);
      return;
    }
    onDone(outcome, t('report.sent'));
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('report.title')}
      description={t('report.description', { name: supplier.name })}
      footer={
        <FormFooter
          className="w-full border-t-0 pt-0"
          form={formId}
          primaryLabel={t('report.submit')}
          primaryDisabled={sending}
          cancelLabel={t('actions.cancel')}
          onCancel={() => onOpenChange(false)}
        />
      }
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <RadioGroup legend={t('report.reasonLegend')}>
          {FLAG_REASONS.map((choice) => (
            <RadioOption
              key={choice}
              name={`${formId}-reason`}
              label={t(`flag.reason.${choice}`)}
              {...(choice === 'duplicate' ? { help: t('report.duplicateHelp') } : {})}
              checked={reason === choice}
              onSelect={() => setReason(choice)}
            />
          ))}
          {tried && reason === null ? (
            <span role="alert" className="text-destructive text-sm">
              {t('report.reasonRequired')}
            </span>
          ) : null}
        </RadioGroup>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={noteId} className="text-sm font-semibold">
            {t('report.note')}{' '}
            <span className="text-muted-foreground font-normal">
              {reason === 'other' ? t('report.required') : t('report.optional')}
            </span>
          </label>
          <textarea
            id={noteId}
            value={note}
            maxLength={1000}
            rows={3}
            placeholder={t('report.notePlaceholder')}
            aria-invalid={tried && noteMissing}
            onChange={(event) => setNote(event.target.value)}
            className="border-input bg-card rounded-md border px-3 py-2 text-sm"
          />
          {tried && noteMissing ? (
            <span role="alert" className="text-destructive text-sm">
              {t('report.noteRequired')}
            </span>
          ) : null}
        </div>
        {failed ? (
          <p role="alert" className="text-destructive text-sm">
            {t('report.failed')}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
